import type { Hitbox } from "@common/utils/hitbox";
import { Vec, type Vector } from "@common/utils/vector";

/**
 * The rooms of the gunfire's echo (acoustics.ts), drawn as impulse responses, and how much of each the listener hears
 * where they are: pure, so they can be checked (tests/src/combatSounds.test.ts).
 *
 * 0.86 (the owner: "i feel like shot sounds now have too much echo ... especially in open spaces, sounds weird"): out
 * in the open a shot is nearly dry now. 0.84's one outdoor room had six discrete slaps 45 to 300 ms after every shot
 * and a long roll, at full level wherever the listener stood, and a far shot was mostly echo: a flutter of slaps off
 * walls that weren't there. Now the open has only a faint, dark, distant roll; between buildings the shot comes back
 * off them (a dense, short cluster of reflections that the ear hears as one, and a short tail), as much as there are
 * walls round the listener (`enclosureOf`); and the echo's share no longer outgrows the shot with distance in the open
 * (`echoMix`'s `law`).
 */

/** a seeded random (the rooms are the same every visit) */
const mulberry32 = (seed: number) => (): number => {
    let t = (seed = (seed + 0x6D2B79F5) | 0);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

export interface RoomShape {
    readonly seconds: number
    /** when the diffuse tail starts, and how quickly it rises (seconds) */
    readonly tailAt: number
    readonly tailRise: number
    /** its decay time constant (seconds): the tail is down 60 dB after about 6.9 times this */
    readonly tau: number
    /** how dark it starts and how fast it darkens (a one-pole lowpass's coefficient: 0 open, towards 1 dark) */
    readonly dampStart: number
    readonly dampRate: number
    /** discrete reflections: [seconds, gain, how far to one side (-1 left, 1 right)] */
    readonly reflections: ReadonlyArray<readonly [number, number, number]>
    /** the share of the echo's energy in those reflections (the rest is the tail) */
    readonly early: number
    readonly seed: number
}

/**
 * open ground: no walls to give the shot back, only the land and the treelines far off. A faint roll that swells and
 * dies away, dark (the far echoes lose their highs on the way), no discrete reflection at all
 */
export const OPEN: RoomShape = {
    seconds: 1.5,
    tailAt: 0.1,
    tailRise: 0.28,
    tau: 0.27,
    dampStart: 0.78,
    dampRate: 0.12,
    reflections: [],
    early: 0,
    seed: 0x09e0
};

/**
 * between buildings: the walls round the listener give the shot back at once, a dozen reflections in the first 90 ms,
 * too close together to hear apart (the ear fuses them into one "slap" of the street), and a short, darkening tail
 */
export const TOWN: RoomShape = {
    seconds: 1,
    tailAt: 0.02,
    tailRise: 0.05,
    tau: 0.12,
    dampStart: 0.45,
    dampRate: 0.5,
    reflections: [
        [0.009, 0.5, -0.5], [0.014, 0.42, 0.6], [0.019, 0.4, -0.2], [0.026, 0.36, 0.8], [0.031, 0.33, -0.7], [0.038, 0.3, 0.3],
        [0.046, 0.27, -0.4], [0.053, 0.24, 0.5], [0.061, 0.21, -0.8], [0.069, 0.18, 0.1], [0.078, 0.15, 0.7], [0.088, 0.12, -0.3]
    ],
    early: 0.5,
    seed: 0x7e01
};

/** inside: a hard room's dense early reflections and a brighter, shorter tail */
export const INDOORS: RoomShape = {
    seconds: 1.1,
    tailAt: 0.008,
    tailRise: 0.02,
    tau: 0.13,
    dampStart: 0.2,
    dampRate: 0.45,
    reflections: [[0.006, 0.6, 0.5], [0.009, 0.5, -0.6], [0.013, 0.45, 0.2], [0.018, 0.38, -0.3], [0.024, 0.32, 0.7], [0.031, 0.26, -0.8], [0.039, 0.2, 0.1]],
    early: 0.35,
    seed: 0x1d00
};

const energyOf = (data: Float32Array): number => {
    let energy = 0;
    for (const sample of data) energy += sample * sample;
    return energy;
};

/**
 * A room's impulse response, in stereo (the two sides drawn apart, so it's wide). The tail: noise that rises, then dies
 * away, darker as it goes. The reflections: each a burst of a few milliseconds (a wall gives back a smeared, duller
 * copy), louder on its own side. Each part gets its share of the energy, and the whole of each side 1: how loud the
 * echo is, is set by the sends and the mix alone.
 */
export function roomImpulse(context: Pick<BaseAudioContext, "sampleRate" | "createBuffer">, shape: RoomShape): AudioBuffer {
    const rate = context.sampleRate;
    const length = Math.ceil(shape.seconds * rate);
    const buffer = context.createBuffer(2, length, rate);
    const burst = Math.max(8, Math.round(0.003 * rate));
    const allGain = shape.reflections.reduce((sum, [, gain]) => sum + gain * gain, 0) || 1;
    for (let channel = 0; channel < 2; channel++) {
        const data = buffer.getChannelData(channel);
        const random = mulberry32(shape.seed + channel * 7919);
        let low = 0;
        for (let i = 0; i < length; i++) {
            const t = i / rate;
            const rise = t < shape.tailAt ? 0 : Math.min(1, (t - shape.tailAt) / shape.tailRise);
            const fall = Math.exp(-Math.max(0, t - shape.tailAt) / shape.tau);
            const damp = Math.min(0.97, shape.dampStart + shape.dampRate * t);
            low = low * damp + (random() * 2 - 1) * (1 - damp);
            // the end faded to nothing (no click when a convolver's tail runs out)
            const end = Math.min(1, (length - i) / (0.05 * rate));
            data[i] = low * rise * fall * end;
        }
        const tailGain = Math.sqrt((1 - shape.early) / Math.max(energyOf(data), 1e-12));
        for (let i = 0; i < length; i++) data[i] *= tailGain;
        for (const [at, gain, side] of shape.reflections) {
            const start = Math.round(at * rate);
            const sideGain = gain * (channel === 0 ? 1 - Math.max(0, side) * 0.6 : 1 + Math.min(0, side) * 0.6);
            const piece = new Float32Array(burst);
            const damp = Math.min(0.9, shape.dampStart + shape.dampRate * at);
            let soft = 0;
            for (let k = 0; k < burst; k++) {
                soft = soft * damp + (random() * 2 - 1) * (1 - damp);
                piece[k] = soft * Math.exp(-k / (burst / 3)) * Math.min(1, k / 3);
            }
            const pieceGain = Math.sqrt(shape.early * (sideGain * sideGain) / allGain / Math.max(energyOf(piece), 1e-12));
            for (let k = 0; k < burst && start + k < length; k++) data[start + k] += piece[k] * pieceGain;
        }
        const norm = 1 / Math.sqrt(Math.max(energyOf(data), 1e-12));
        for (let i = 0; i < length; i++) data[i] *= norm;
    }
    return buffer;
}

// ------------------------------------------------------------------------------------------------- where you stand

/** something round the listener that gives a shot back: a building's walls, a wall, a container, a rock, a tree */
export interface Reflector {
    readonly hitbox: Hitbox
    /** how much it gives back: 1 a building or a wall, less a tree */
    readonly weight: number
}

/** how far round the listener the walls count (game units), and in how many directions it looks */
export const ENCLOSURE_REACH = 48;
export const ENCLOSURE_RAYS = 16;

/**
 * How walled in the listener is, from 0 (open ground) to 1 (a street lined both sides, a yard between blocks): it looks
 * out in `rays` directions as far as `reach`, and each direction counts the strongest return along it (the nearest
 * reflector's weight, less the further off it is). Half the directions blocked close by is enclosed enough for the
 * whole echo.
 */
export function enclosureOf(origin: Vector, reflectors: readonly Reflector[], rays = ENCLOSURE_RAYS, reach = ENCLOSURE_REACH): number {
    if (reflectors.length === 0) return 0;
    let sum = 0;
    for (let i = 0; i < rays; i++) {
        const a = (i / rays) * Math.PI * 2;
        const end = Vec(origin.x + Math.cos(a) * reach, origin.y + Math.sin(a) * reach);
        let best = 0;
        for (const { hitbox, weight } of reflectors) {
            let hit: ReturnType<Hitbox["intersectsLine"]>;
            try {
                hit = hitbox.intersectsLine(origin, end);
            } catch {
                // (a polygon can't be crossed by a line: it doesn't count)
                continue;
            }
            if (!hit) continue;
            const distance = Vec.len(Vec.sub(hit.point, origin));
            const strength = weight * Math.max(0, 1 - distance / reach) ** 0.6;
            if (strength > best) best = strength;
        }
        sum += best;
    }
    return Math.min(1, (sum / rays) * 1.8);
}

export type Place = "outdoors" | "indoors" | "underground";

/** the echo's mix where the listener is: each room's level, and how the echo's share grows with distance (`law`) */
export interface EchoMix {
    readonly open: number
    readonly town: number
    readonly indoor: number
    /**
     * a sound's send is its amount times its direct level to this power (combatSoundsLogic.roomSend): 1 keeps the echo
     * the same share of the shot near and far; lower lets a far shot be more echo. In the open the echo hardly grows
     * with distance (there's nothing to give a far shot back); in a street or a room it does
     */
    readonly law: number
}

/** how loud the open's faint roll is, at most (on open ground: it fades as walls take over) */
export const OPEN_LEVEL = 0.4;

/**
 * how walled in the listener must be before the street's echo comes in at all (0.88): a rock or two beside someone in
 * a field (enclosure 0.3 to 0.4) brought back 30 to 40% of a street's echo there (the audit of 0.87, against the owner's
 * open ground nearly dry); now 12 to 25%, a single wall about half, a street all of it
 */
export const ENCLOSURE_KNEE = 0.2;

export function echoMix(place: Place, enclosure: number): EchoMix {
    const e = Math.min(1, Math.max(0, (enclosure - ENCLOSURE_KNEE) / (1 - ENCLOSURE_KNEE)));
    switch (place) {
        // (a room's walls give a shot back hard: its reflections a good share of it, more underground; a little less than
        // 0.84 and 0.85 gave, as the owner found the echo too much, if most of all in the open)
        case "underground": return { open: 0, town: 0, indoor: 1.5, law: 0.55 };
        case "indoors": return { open: 0, town: 0.25, indoor: 1.15, law: 0.6 };
        case "outdoors": return { open: OPEN_LEVEL * (1 - e), town: e, indoor: 0, law: 0.9 - 0.25 * e };
    }
}
